const UseFormUtils = module.exports = {};
const { castBool } = require('utils/typecast');

UseFormUtils.buildChoiceOptions = (values = []) => values.map((value) => ({
    value,
    label: String(value)
        .split('-')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ')
}));

UseFormUtils.getPath = (obj, path) => String(path || '').split('.').reduce((current, key) => {

    if (!key) {
        return current;
    }

    return current?.[key];
}, obj);

UseFormUtils.buildNamedRefs = ({
    createRef,
    names = []
}) => {

    return names.reduce((collector, name) => {

        collector[name] = createRef();
        return collector;
    }, {});
};

UseFormUtils.stripNumericCharacters = (value = '') => String(value).replace(/[0-9]/g, '');

UseFormUtils.applyBooleanDefault = (value, defaultValue) => {

    if (typeof value === 'undefined' || value === null) {
        return defaultValue;
    }

    return castBool(value);
};

UseFormUtils.conditionTypes = {
    always: () => true,
    never: () => false,
    parentOnly: ({ props }) => !!props?.isParent,
    notParent: ({ props }) => !props?.isParent,
    signupOnly: ({ props }) => props?.mode === 'signup',
    editOnly: ({ props }) => props?.mode === 'edit',
    companyOnly: ({ props }) => !!props?.isCompany,
    communityOnly: ({ props }) => !!props?.isCommunity,
    schoolOnly: ({ props }) => !props?.isCompany && !props?.isCommunity
};

UseFormUtils.hasRolePermission = ({
    props,
    permission
}) => !!props?.rolePermissions?.[permission];

UseFormUtils.matchesProp = ({
    props,
    path,
    value
}) => {

    const actual = UseFormUtils.getPath(props, path);

    if (typeof value === 'undefined') {
        return !!actual;
    }

    return actual === value;
};

UseFormUtils.evaluateCondition = ({
    condition = 'always',
    context = {},
    conditionTypes = UseFormUtils.conditionTypes
}) => {

    if (!condition) {
        return true;
    }

    if (typeof condition === 'function') {
        return !!condition(context);
    }

    if (typeof condition === 'string') {
        const check = conditionTypes[condition];
        return check ? !!check(context) : false;
    }

    if (Array.isArray(condition)) {
        return condition.every((item) => UseFormUtils.evaluateCondition({
            condition: item,
            context,
            conditionTypes
        }));
    }

    if (condition.all) {
        return condition.all.every((item) => UseFormUtils.evaluateCondition({
            condition: item,
            context,
            conditionTypes
        }));
    }

    if (condition.any) {
        return condition.any.some((item) => UseFormUtils.evaluateCondition({
            condition: item,
            context,
            conditionTypes
        }));
    }

    if (condition.not) {
        return !UseFormUtils.evaluateCondition({
            condition: condition.not,
            context,
            conditionTypes
        });
    }

    if (condition.permission) {
        return UseFormUtils.hasRolePermission({
            props: context.props,
            permission: condition.permission
        });
    }

    if (condition.prop) {
        return UseFormUtils.matchesProp({
            props: context.props,
            path: condition.prop,
            value: condition.value
        });
    }

    if (condition.mode) {
        return context.props?.mode === condition.mode;
    }

    return false;
};

UseFormUtils.visibleWhen = ({
    condition,
    conditionTypes = UseFormUtils.conditionTypes
}) => (context) => UseFormUtils.evaluateCondition({
    condition,
    context,
    conditionTypes
});

UseFormUtils.visibleWhenAnyChildVisible = ({
    getChildren = (item) => item?.fields || []
} = {}) => (context = {}) => {
    const {
        item
    } = context;
    const children = getChildren(item);

    if (!Array.isArray(children) || !children.length) {
        return false;
    }

    return children.some((child) => UseFormUtils.evaluateCondition({
        condition: child.visibleWhen,
        context,
        conditionTypes: UseFormUtils.conditionTypes
    }));
};
